import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShip,
  faGraduationCap,
  faIdCard,
  faAnchor,
  faExchangeAlt,
  faShoppingCart,
  faTimes,
  faEye,
  faSailboat,
  faIndustry,
  faStar,
  faArrowRight,
  faUsers,
  faXmark,
  faUser,
  faKey,
  faRegistered,
  faGift,
  faCreditCard
} from '@fortawesome/free-solid-svg-icons';
import './DashBoard.css';
import { useGet } from '../Hooks/useFetch';
import useAuth from '../Hooks/useAuth';
import DataLoader from '../Components/DataLoader';
import SubmitLoader from '../Components/SubmitLoader';
import NewBoatPopupMessage from '../Components/BoatRegistration/NewBoatPopupMessage';
import '../Components/Modal/Modal.css';

const DashBoard = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [res, err, isLoading, apiGet] = useGet();
  const [dashboardData, setDashboardData] = useState(null);
  
  // New Boat Popup states
  const [newBoatMsgOpen, setNewBoatMsgOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMsg, setDialogMsg] = useState('');
  
  // Payment states
  const [licencePaymentRes, licencePaymentErr, licencePaymentLoading, apiGetLicencePayment] = useGet();
  const [isLicenceModalOpen, setIsLicenceModalOpen] = useState(false);
  
  const [mooringPaymentRes, mooringPaymentErr, mooringPaymentLoading, apiGetMooringPayment] = useGet();
  const [vesselPaymentRes, vesselPaymentErr, vesselPaymentLoading, apiGetVesselPayment] = useGet();
  
  const [mooringPayments, setMooringPayments] = useState({});
  const [vesselPayments, setVesselPayments] = useState({});
  const [currentMooringRegoNo, setCurrentMooringRegoNo] = useState(null);
  const [currentVesselRegoNo, setCurrentVesselRegoNo] = useState(null);

  useEffect(() => {
    apiGet('/api/pna/Dashboard');
  }, []);

  useEffect(() => {
    if (res) {
      setDashboardData(res);
    }
  }, [res]);

  // Handle licence payment data loaded
  useEffect(() => {
    if (licencePaymentRes && !licencePaymentErr) {
      setIsLicenceModalOpen(true);
    } else if (licencePaymentErr) {
      alert('Error fetching licence payment data: ' + licencePaymentErr);
    }
  }, [licencePaymentRes, licencePaymentErr]);

  // Handle mooring payment data loaded
  useEffect(() => {
    if (mooringPaymentRes && !mooringPaymentErr && currentMooringRegoNo) {
      if (mooringPaymentRes.isRenewAble) {
        setMooringPayments(prev => ({ ...prev, [currentMooringRegoNo]: mooringPaymentRes }));
        handlePaymentSubmit(mooringPaymentRes);
      } else {
        alert(mooringPaymentRes.reason || 'Unable to renew this mooring');
      }
      setCurrentMooringRegoNo(null);
    } else if (mooringPaymentErr) {
      alert('Error fetching mooring payment data: ' + mooringPaymentErr);
      setCurrentMooringRegoNo(null);
    }
  }, [mooringPaymentRes, mooringPaymentErr]);

  // Handle vessel payment data loaded
  useEffect(() => {
    if (vesselPaymentRes && !vesselPaymentErr && currentVesselRegoNo) {
      if (vesselPaymentRes.isRenewAble) {
        setVesselPayments(prev => ({ ...prev, [currentVesselRegoNo]: vesselPaymentRes }));
        handlePaymentSubmit(vesselPaymentRes);
      } else {
        alert(vesselPaymentRes.reason || 'Unable to renew this vessel');
      }
      setCurrentVesselRegoNo(null);
    } else if (vesselPaymentErr) {
      alert('Error fetching vessel payment data: ' + vesselPaymentErr);
      setCurrentVesselRegoNo(null);
    }
  }, [vesselPaymentRes, vesselPaymentErr]);

  // Handle payment submission
  const handlePaymentSubmit = (paymentData, needCard = false) => {
    const formHiddenData = {
      cd_community: "SERVICETAS",
      cd_currency: "AUD",
      CP_billerCode: "8223",
      no_reference: paymentData?.reference,
      am_pre_surcharge: needCard === true 
        ? (paymentData?.amount + paymentData?.cardAmount).toFixed(2) 
        : paymentData?.amount?.toFixed(2),
      CP_emailAddress: paymentData?.email,
    };

    const tempForm = document.createElement("form");
    tempForm.action = "https://quickweb.westpac.com.au/OnlinePaymentServlet";
    tempForm.method = "post";
    tempForm.target = "_blank";

    for (let key in formHiddenData) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = key;
      input.value = formHiddenData[key];
      tempForm.appendChild(input);
    }

    document.body.appendChild(tempForm);
    tempForm.submit();
    document.body.removeChild(tempForm);
  };

  // Handle licence renewal click
  const handleLicenceRenewClick = () => {
    if (!licencePaymentRes) {
      apiGetLicencePayment('/api/licence/RenewData');
    } else {
      setIsLicenceModalOpen(true);
    }
  };

  // Handle mooring renewal click
  const handleMooringRenewClick = (moRegoNo) => {
    if (!mooringPayments[moRegoNo]) {
      setCurrentMooringRegoNo(moRegoNo);
      apiGetMooringPayment(`/api/mooring/RenewData/${moRegoNo}`);
    } else {
      handlePaymentSubmit(mooringPayments[moRegoNo]);
    }
  };

  // Handle vessel renewal click
  const handleVesselRenewClick = (pcRegoNo) => {
    if (!vesselPayments[pcRegoNo]) {
      setCurrentVesselRegoNo(pcRegoNo);
      apiGetVesselPayment(`/api/pcraft/RenewData/${pcRegoNo}`);
    } else {
      handlePaymentSubmit(vesselPayments[pcRegoNo]);
    }
  };
  // Check popup message for new vessel registration
  const checkPopupMsg = () => {
    if (auth?.isAblePrintOrRegister === true) {
      setNewBoatMsgOpen(true);
      setOpenDialog(false);
    } else {
      setNewBoatMsgOpen(false);
      setOpenDialog(true);
      setDialogMsg("You have recently made changes to personal details. Some functions (Printing Certificates, New Vessel Registrations) are unavailable until MAST have confirmed these changes.");
    }

    if(auth?.isPnaIdVerified === true) {
      setNewBoatMsgOpen(true);
      setOpenDialog(false);
    }else {
      setNewBoatMsgOpen(false);
      setOpenDialog(true);
      setDialogMsg("Your account with MAST is not yet Verified. You will be unable to complete any transactions involving boat registration until you provide identity information to Service Tasmania or MAST.");
    }
  };
  // Icon mapping
  const iconMap = {
    faShip,
    faGraduationCap,
    faIdCard,
    faAnchor,
    faExchangeAlt,
    faShoppingCart,
    faTimes,
    faEye,
    faSailboat,
    faIndustry,
    faUser,
    faKey,
    faRegistered,
    faGift,
    faCreditCard
  };

  const handleNavigation = (path, serviceTitle) => {
    // For Renew Licence, don't navigate - the WestpacPayment component handles it
    if (serviceTitle === 'Renew Licence') {
      return;
    }
    // For Register New Vessel, show popup message
    if (serviceTitle === 'Register New Vessel') {
      checkPopupMsg();
      return;
    }
    navigate(path);
  };

  if (isLoading) {
    return <SubmitLoader isLoading={isLoading} />;
  }

  if (err) {
    return <div className='error-msg-container'><div className="dashboard-error-message">{err}</div></div>;
  }

  if (!dashboardData) {
    return null;
  }

  const popularServices = dashboardData.services?.filter(s => {
    if (s.isPopular) {
      // Only show "Renew Licence" if licence exists and is renewable
      if (s.title === 'Renew Licence') {
        return dashboardData.licence && dashboardData.licence.isLicenceRenewable === true;
      }
      return true;
    }
    return false;
  }) || [];
  const allServices = dashboardData.services?.filter(s => !s.isPopular) || [];
  const moorings = dashboardData.moorings?.filter(m => m.isMooringRenewable === true) || [];
  const vessels = dashboardData.vessels || [];
  const renewableVessels = vessels.filter(vessel => vessel.isVesselRenewable === true);

  return (
    <div className="dashboard-container">
      {/* Popular Services Section */}
      <section className="dashboard-services-section dashboard-popular-section">
        <div className="dashboard-section-header">
          <div className="dashboard-section-title-group">
            <FontAwesomeIcon icon={faStar} className="dashboard-section-icon" />
            <h2 className="dashboard-section-title">Popular Services</h2>
          </div>
        </div>

        <div className="dashboard-services-grid dashboard-popular-grid">
          {popularServices.map((service) => {
            const serviceIcon = iconMap[service.icon] || faStar;
            const isRenewLicence = service.title === 'Renew Licence';
            const isDisabled = service.isDisabled === true;
            
            return (
              <div 
                key={service.id} 
                className={`dashboard-service-card dashboard-popular-card${isDisabled ? ' dashboard-card-disabled' : ''}`}
                onClick={() => !isRenewLicence && !isDisabled && handleNavigation(service.path, service.title)}
                style={{ '--card-gradient': service.gradient, cursor: (isRenewLicence || isDisabled) ? 'auto' : 'pointer' }}
              >
                <div className="dashboard-card-header">
                  <div 
                    className="dashboard-service-icon dashboard-popular-icon"
                    style={{ background: service.gradient }}
                  >
                    <FontAwesomeIcon icon={serviceIcon} />
                  </div>
                  <h3 className="dashboard-service-title" style={service.title === 'Licence Payment Due' ? { color: '#e74c3c' } : {}}>{service.title}</h3>
                </div>
                <div className="dashboard-card-content">
                  <p className="dashboard-service-description" dangerouslySetInnerHTML={{ __html: service.description }}></p>
                </div>
                <div className="dashboard-card-footer">
                  {isDisabled ? null : isRenewLicence ? (
                    <div 
                      className="dashboard-service-action" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLicenceRenewClick();
                      }}
                      style={{ cursor: licencePaymentLoading ? 'wait' : 'pointer' }}
                    >
                      {licencePaymentLoading ? (
                        <DataLoader isLoading={true} />
                      ) : (
                        <>
                          <span>Go</span>
                          <FontAwesomeIcon icon={faArrowRight} />
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="dashboard-service-action">
                      <span>Go</span>
                      <FontAwesomeIcon icon={faArrowRight} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Vessel Renewals Sub-Section */}
      {renewableVessels && renewableVessels.length > 0 && (
        <section className="dashboard-services-section dashboard-vessels-section dashboard-sub-section">
          <div className="dashboard-section-header">
            <div className="dashboard-section-title-group">
              <FontAwesomeIcon icon={faSailboat} className="dashboard-section-icon" />
              <h2 className="dashboard-section-title">My Vessels - Renewals</h2>
            </div>
          </div>

          <div className="dashboard-renewals-list">
            {renewableVessels.map((vessel) => (
              <div key={vessel.pcRegoNo} className="dashboard-renewal-card dashboard-vessel-card">
                <div className="dashboard-renewal-header">
                  <div className="dashboard-renewal-icon" style={{ backgroundColor: '#3498db' }}>
                    <FontAwesomeIcon icon={faSailboat} />
                  </div>
                  <div className="dashboard-renewal-info">
                    <h3 className="dashboard-renewal-title">{vessel.pcName || 'Unnamed Vessel'}</h3>
                    <p className="dashboard-renewal-rego">Registration: {vessel.pcRegoNo}</p>
                    {vessel.pcExpiryDate && (
                      <p className="dashboard-renewal-expiry">
                        Expires: {new Date(vessel.pcExpiryDate).toLocaleDateString()}
                      </p>
                    )}
                    {vessel.pcStatusDesc && (
                      <p className="dashboard-renewal-status">Status: {vessel.pcStatusDesc}</p>
                    )}
                  </div>
                </div>
                <div className="dashboard-renewal-action">
                  <div 
                    className="dashboard-service-action"
                    onClick={() => handleVesselRenewClick(vessel.pcRegoNo)}
                  >
                    <span>Get Started</span>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Mooring Renewals Sub-Section */}
      {moorings && moorings.length > 0 && (
        <section className="dashboard-services-section dashboard-moorings-section dashboard-sub-section">
          <div className="dashboard-section-header">
            <div className="dashboard-section-title-group">
              <FontAwesomeIcon icon={faAnchor} className="dashboard-section-icon" />
              <h2 className="dashboard-section-title">My Moorings - Renewals</h2>
            </div>
          </div>

          <div className="dashboard-renewals-list">
            {moorings.map((mooring) => (
              <div key={mooring.moRegoNo} className="dashboard-renewal-card dashboard-mooring-card">
                <div className="dashboard-renewal-header">
                  <div className="dashboard-renewal-icon" style={{ backgroundColor: '#f39c12' }}>
                    <FontAwesomeIcon icon={faAnchor} />
                  </div>
                  <div className="dashboard-renewal-info">
                    <h3 className="dashboard-renewal-title">Mooring {mooring.moRegoNo}</h3>
                    <p className="dashboard-renewal-location">
                      {mooring.moLocation1} {mooring.moLocation2}
                    </p>
                    {mooring.moExpiryDate && (
                      <p className="dashboard-renewal-expiry">
                        Expires: {new Date(mooring.moExpiryDate).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
                <div className="dashboard-renewal-action">
                  <div 
                    className="dashboard-service-action"
                    onClick={() => handleMooringRenewClick(mooring.moRegoNo)}
                  >
                    <span>Get Started</span>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* All Services Section */}
      <section className="dashboard-services-section dashboard-all-services-section">
        <div className="dashboard-section-header">
          <div className="dashboard-section-title-group">
            <FontAwesomeIcon icon={faUsers} className="dashboard-section-icon" />
            <h2 className="dashboard-section-title">All Services</h2>
          </div>
        </div>

        <div className="dashboard-services-grid dashboard-all-services-grid">
          {allServices.map((service) => {
            const serviceIcon = iconMap[service.icon] || faStar;
            
            return (
              <div 
                key={service.id} 
                className="dashboard-service-card dashboard-all-service-card"
                onClick={() => handleNavigation(service.path, service.title)}
              >
                <div className="dashboard-card-header">
                  <div 
                    className="dashboard-service-icon"
                    style={{ backgroundColor: service.color }}
                  >
                    <FontAwesomeIcon icon={serviceIcon} />
                  </div>
                  <span className="dashboard-service-category">{service.category}</span>
                </div>
                <div className="dashboard-card-content">
                  <h3 className="dashboard-service-title">{service.title}</h3>
                  <p className="dashboard-service-description" dangerouslySetInnerHTML={{ __html: service.description }}></p>
                </div>
                <div className="dashboard-card-footer">
                  <div className="dashboard-service-action">
                    <span>Go</span>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Licence Renewal Modal */}
      {isLicenceModalOpen && licencePaymentRes && (
        <div className="modalBackground">
          <div className="modalContainer">
            <div className="modal-header">
              <div className="modal-title">Licence Card</div>
              <div className="titleCloseBtn">
                <FontAwesomeIcon
                  icon={faXmark}
                  className="xmark-icon"
                  size="2x"
                  onClick={() => setIsLicenceModalOpen(false)}
                />
              </div>
            </div>
            <div className="modal-body">
              <div className="modal-licence">
                Do you also want to purchase a licence card?
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => {
                  handlePaymentSubmit(licencePaymentRes, false);
                  setIsLicenceModalOpen(false);
                }}
                className="cancelBtn"
                type="button"
              >
                No (+$0.00)
              </button>
              <button
                type="button"
                className="approvalBtn"
                onClick={() => {
                  handlePaymentSubmit(licencePaymentRes, true);
                  setIsLicenceModalOpen(false);
                }}
              >
                Yes (+${licencePaymentRes?.cardAmount?.toFixed(2)})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Boat Popup Message */}
      <NewBoatPopupMessage
        isOpen={newBoatMsgOpen}
        setIsOpen={setNewBoatMsgOpen}
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        dialogMsg={dialogMsg}
      />
    </div>
  );
};

export default DashBoard;